<template>
  <div class="visualization">
    <template v-if="table">
      <data-table
        v-if="!loading && hasData"
        class="visualization__content table-viz"
        :items="items"
        sort-by="key"
        :pagination-side="1"
        :disable-filtering="true"
        :pagination="['top']"
        :page.sync="page"
        :theme="$_ui_theme_tables"
        :per-page="perPage"
        @loaded="tableLoaded"
      >
        <data-column
          v-for="(col, index) in uniqueColumns"
          :key="index"
          :field="tableFields[col] || col"
          :label="col"
          :sortable="false"
        >
          <template slot-scope="props">
            <template v-if="(tableFields[col] || col) === '#timestamp'">
              {{ format(new Date(props.item[tableFields[col] || col]), "dd/MM/yyyy HH:mm:ss") }}
            </template>

            <template v-else-if="props.item[tableFields[col] || col] instanceof Array">
              <div class="tags">
                <a
                  v-for="(v, index) in tableCellArray(props.item[tableFields[col] || col])"
                  :key="index"
                >
                  {{ v }}
                </a>
              </div>
            </template>

            <template v-else>
              <div style="width: auto">
                <popover
                  placement="right"
                  width="300px"
                  style="padding: 0"
                  @shown="shown(props.item[tableFields[col] || col], tableFields[col] || col)"
                >
                  <a
                    slot="trigger"
                    class="is-text break-text"
                    href="#"
                    @click.prevent
                  >
                    <span>{{ props.item[tableFields[col] || col] }}</span>
                  </a>

                  <template v-slot:default="slotProps">
                    <data-table
                      v-if="slotProps.active"
                      :items="searchItems"
                      disable-filtering
                      class="hidden-y"
                      :theme="$_ui_theme_tables"
                      :pagination="[]"
                    >
                      <data-column
                        field="value"
                        label="Value"
                        :sortable="false"
                      />

                      <data-column
                        field="count"
                        label="Count"
                        :sortable="false"
                      />
                    </data-table>

                    <div
                      class="has-text-left is-size-7"
                      style="margin-top: -0.6em"
                    >
                      <div class="menu sidebar-menu">
                        <div class="menu-list-wrapper">
                          <ul
                            class="menu-list"
                            style="padding: 0; font-size: 0.9em"
                          >
                            <li class="menu-item">
                              <a href="#" @click.prevent="searchByValue">
                                <octicon :icon="search" />
                                Search with Value
                              </a>
                            </li>

                            <li class="menu-item">
                              <a href="#" @click.prevent="searchByTime">
                                <octicon :icon="search" />
                                Search value By Time
                              </a>
                            </li>

                            <li class="menu-item">
                              <a href="#" @click.prevent="customClick">
                                <octicon :icon="search" />
                                Open Custom View
                              </a>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </template>
                </popover>
              </div>
            </template>
          </template>
        </data-column>
      </data-table>

      <template v-else-if="!loading && !hasData">
        <div
          style="
            height: 100%;
            width: 100%;
            position: relative;
            min-height: 150px;
          "
        >
          <chart-error :error="$t('chart.errorNoData')" />
        </div>
      </template>
    </template>

    <template v-else-if="sum">
      <div class="visualization__content">
        <p class="has-text-centered has-text-weight-bold widget-sum-display">
          <a href="#" style="color: inherit" @click.prevent="lableClick">
            <span class="widget-sum-number">{{ total | abbr }}</span>
            <span class="widget-sum-unit">{{ unit }}</span>
          </a>
        </p>
      </div>
    </template>

    <template v-else-if="map">
      <world-map
        ref="worldMap"
        class="visualization__content"
        :data="data"
        :loading="loading"
      />
    </template>

    <template v-else-if="bpmap">
      <bp-map
        ref="bpMap"
        class="visualization__content"
        :data="data"
        :loading="loading"
      />
    </template>

    <chart
      v-else-if="type"
      ref="chart"
      class="visualization__content"
      :loading="loading"
      :query-data="fullQuery"
      :series="series"
      :type="type"
      :style="{ height: chartHeight }"
    />

    <div class="visualization__footer">
      <slot name="below-chart" :sum="sum" />
    </div>
  </div>
</template>

<script>
import format from "date-fns/format";
import { search } from "octicons-vue";
import { getName } from "country-list";
import { DataTable, DataColumn } from "@cyradar/ui";
import { makeLineData, makePieData, makeBarData } from "@/utils";
import Chart from "@/components/Chart";
import WorldMap from "@/components/WorldMap";
import ChartError from "@/components/ChartError";
import BPMap from "@/components/BPMap";
export default {
  components: {
    Chart,
    WorldMap,
    "bp-map": BPMap,
    DataTable,
    DataColumn,
    ChartError,
  },
  filters: {
    abbr(value) {
      if (value >= 1e6) {
        return (value / 1e6).toFixed(3) + " M";
      }

      return value.toLocaleString();
    },
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    chartHeight: {
      type: [String, Number],
      default: "100%",
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loaded: false,
      searchValue: "",
      searchField: "",
      page: 1,
      perPage: 10,
    };
  },
  computed: {
    search() {
      return search;
    },
    uniqueColumns() {
      const cols = this.data.columns || this.data.fields || [];
      return [...new Set(cols)];
    },
    tableFields() {
      // Return mapping of alias field to actual field for table columns
      const columns = this.data.columns || this.data.fields || [];
      if (!this.data || !columns.length) {
        return {};
      }
      
      const mapping = {};
      
      // If we have alias_fields, map alias_fields to actual fields
      if (this.data.alias_fields && this.data.alias_fields.length > 0 && this.data.fields) {
        columns.forEach((alias, index) => {
          mapping[alias] = this.data.fields[index] || alias;
        });
      } else {
        // If no alias_fields, columns are the same as fields, so no mapping needed
        columns.forEach((col) => {
          mapping[col] = col;
        });
      }
      
      return mapping;
    },
    type() {
      return this.data && this.data.type;
    },
    fullQuery() {
      if (!this.data.query) {
        return {
          query: "",
          range: this.data.range,
        };
      }
      return {
        query: this.data.query,
        range: this.data.range,
        start: this.data.start,
        end: this.data.end,
      };
    },
    filterQuery() {
      if (!this.data || !this.data.query) {
        return "";
      }

      const queryMatches = this.data.query.match(/(.+?)\|.+/m);
      if (!queryMatches || queryMatches.length < 2) {
        return "";
      }
      return queryMatches[1].trim();
    },
    indexQuery() {
      if (!this.data || !this.data.query) {
        return "";
      }

      const matches = this.data.query.match(/^(index:(.*?))(\|(.*))?$/m);
      if (!matches) {
        return "";
      }

      return matches[1];
    },
    unit() {
      if (!this.data && !this.data.unit) {
        return "";
      } else {
        return this.data.unit;
      }
    },
    sum() {
      return this.type === "sum";
    },
    table() {
      return this.type === "table";
    },
    pie() {
      return this.type === "pie";
    },
    bar() {
      return this.type === "bar";
    },
    column() {
      return this.type === "column";
    },
    line() {
      return this.type === "line";
    },
    map() {
      return this.type === "map";
    },
    bpmap() {
      return this.type === "bpmap";
    },
    series() {
      if (!this.data) {
        return {
          columns: [],
        };
      }

      if (this.pie) {
        return makePieData(this.data.events);
      }

      if (this.bar) {
        return makeBarData(this.data.events);
      }

      if (this.column) {
        return makeBarData(this.data.events);
      }

      if (this.line) {
        const x = this.data.events.map((event) => {
          return {
            name: event.key,
            data: (event.data || []).map((hit) => [hit.key, hit.doc_count]),
          };
        });

        return makeLineData(
          x,
          this.data.interval,
          this.data.start,
          this.data.end
        );
      }

      return {
        columns: [],
      };
    },
    total() {
      return (this.data && this.data.total) || 0;
    },
    hasData() {
      return this.data && this.data.events && this.data.events.length;
    },
  },

  watch: {
    data(val, oldVal) {
      if (JSON.stringify(val) !== JSON.stringify(oldVal)) {
        return;
      }

      this.loaded = false;
    },
  },
  mounted() {},
  methods: {
    shown(v, f) {
      this.searchValue = v;
      this.searchField = f;
    },
    escapeSearchValue(value) {
      // If value is null, undefined, or empty string, return as is
      if (!value && value !== 0) {
        return value;
      }

      // Convert to string if not already
      const stringValue = String(value);

      // If value contains special characters that could break query syntax,
      // wrap it in double quotes and escape any existing quotes
      if (/[\s:()[\]{}|&!*?~^"\\]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '\\"')}"`;
      }

      return `"${stringValue}"`;
    },
    addAndToQuery(baseQuery, newCondition) {
      if (!baseQuery || baseQuery.trim() === "") {
        return newCondition;
      }

      const pipeIndex = baseQuery.indexOf("|");
      const searchableQuery = pipeIndex >= 0 ? baseQuery.slice(0, pipeIndex).trim() : baseQuery.trim();
      const pipeQuery = pipeIndex >= 0 ? baseQuery.slice(pipeIndex).trim() : "";
      let nextQuery = "";

      if (/\s+(AND|OR|NOT)\s*$/i.test(searchableQuery)) {
        nextQuery = `${searchableQuery} ${newCondition}`;
      } else if (/^index:\w+\s*$/.test(searchableQuery)) {
        nextQuery = `${searchableQuery} ${newCondition}`;
      } else {
        nextQuery = `${searchableQuery} AND ${newCondition}`;
      }

      return pipeQuery ? `${nextQuery} ${pipeQuery}` : nextQuery;
    },
    routeQuery(q) {
      const query = {
        range: this.data.range,
        q,
      };

      if (!this.data.range && this.data.start && this.data.end) {
        query.start = new Date(this.data.start).toISOString();
        query.end = new Date(this.data.end).toISOString();
      }

      return query;
    },
    format,
    tableCellArray(v) {
      if (!(v instanceof Array)) {
        return v;
      }

      return v
        .map((x) => {
          if (!x.country || !x.country.iso_code) {
            return x;
          }

          return getName(x.country.iso_code);
        })
        .filter((el, pos, arr) => arr.indexOf(el) === pos);
    },
    resize() {
      if (this.$refs.chart) {
        this.$nextTick(() => {
          this.$refs.chart.resize();
        });
      }

      if (this.$refs.worldMap && typeof this.$refs.worldMap.resize === 'function') {
        this.$nextTick(() => {
          this.$refs.worldMap.resize();
        });
      }

      if (this.$refs.bpMap && typeof this.$refs.bpMap.resize === 'function') {
        this.$nextTick(() => {
          this.$refs.bpMap.resize();
        });
      }
    },
    searchByValue() {
      if (this.filterQuery === "") {
        return;
      }

      const escapedValue = this.escapeSearchValue(this.searchValue);
      const queryWithAnd = this.addAndToQuery(this.data.query, `${this.searchField}:${escapedValue}`);
      const routeData = this.$router.resolve({
        name: "search",
        query: this.routeQuery(queryWithAnd),
      });

      const win = window.open(routeData.href, "_blank");
      win.focus();
    },
    searchByTime() {
      if (this.filterQuery === "") {
        return;
      }

      const escapedValue = this.escapeSearchValue(this.searchValue);
      const queryWithAnd = this.addAndToQuery(this.data.query, `${this.searchField}:${escapedValue}`);
      const routeData = this.$router.resolve({
        name: "search",
        query: this.routeQuery(`${queryWithAnd} | timechart by _index`),
      });

      const win = window.open(routeData.href, "_blank");
      win.focus();
    },
    customClick() {
      if (!this.data || !this.data.query || this.data.query === "") {
        return;
      }

      const linkMatches = this.data.query.match(/(.+?)\|.*link (.+?)(?:\||$)/m);
      const subQuery =
        linkMatches && linkMatches.length >= 3 ? ` | ${linkMatches[2]}` : "";

      const escapedValue = this.escapeSearchValue(this.searchValue);
      const queryWithAnd = this.addAndToQuery(this.data.query, `${this.searchField}:${escapedValue}`);
      const routeData = this.$router.resolve({
        name: "search",
        query: this.routeQuery(`${queryWithAnd}${subQuery}`),
      });

      const win = window.open(routeData.href, "_blank");
      win.focus();
    },
    lableClick() {
      if (!this.data || !this.data.query || this.data.query === "") {
        return;
      }

      const routeData = this.$router.resolve({
        name: "search",
        query: this.routeQuery(this.data.query),
      });

      const win = window.open(routeData.href, "_blank");
      win.focus();
    },
    searchItems() {
      if (this.filterQuery === "") {
        return {
          total: 0,
          items: [
            {
              value: this.searchValue,
              count: 0,
            },
          ],
        };
      }

      const escapedValue = this.escapeSearchValue(this.searchValue);
      const queryWithAnd = this.addAndToQuery(this.filterQuery, `${this.searchField}:${escapedValue}`);
      return this.$_search({
        query: `${queryWithAnd}| count by _index`,
        start: this.data.start,
        end: this.data.end,
        pure: true,
      }).then((data) => {
        if (!data) {
          return {
            total: 0,
            items: [
              {
                value: this.searchValue,
                count: 0,
              },
            ],
          };
        }
        return {
          total: 0,
          items: [
            {
              value: this.searchValue,
              count: parseInt(data.total),
            },
          ],
        };
      });
    },
    count() {
      if (!this.data) {
        return Promise.resolve(0);
      }

      if (!this.loaded && this.data.total) {
        return Promise.resolve(this.data.total);
      }

      if (!this.indexQuery) {
        return Promise.resolve(0);
      }

      return this.$_search({
        query: `${this.indexQuery} | count by _index`,
        start: this.data.start,
        end: this.data.end,
        pure: true,
      }).then((data) => {
        if (!data) {
          return 0;
        }

        return parseInt(data.total);
      });
    },
    items(filtering, sorting, { page }) {
      if (!this.data && !this.data.chartType) {
        return {
          total: 0,
          items: [],
        };
      }

      if (this.data.chartType === "count_statistic") {
        this.perPage = this.data.total;

        const toBeFlatten = [];
        this.data.events.forEach((x) => {
          toBeFlatten.push(this.flattenObj(x));
        });

        return {
          total: this.data.total,
          items: toBeFlatten,
        };
      }

      return this.count()
        .then((total) => (total > 10000 ? 10000 : total))
        .then((total) => {
          if (total === 0) {
            return {
              total: 0,
              items: [],
            };
          }

          if (!this.loaded && this.data.events instanceof Array) {
            this.perPage = this.data.events.length;

            const toBeFlatten = [];
            this.data.events.forEach((x) => {
              toBeFlatten.push(this.flattenObj(x));
            });

            return {
              total,
              items: toBeFlatten,
            };
          }

          const payload = {
            query: this.data.query,
            start: this.data.start,
            end: this.data.end,
            from: this.perPage * (page - 1),
          };

          return this.$_search({ ...payload, pure: true }).then((data) => {
            if (!data || !data.data) {
              return {
                total: 0,
                items: [],
              };
            }

            const toBeFlatten = [];
            data.data.forEach((x) => {
              toBeFlatten.push(this.flattenObj(x));
            });

            this.perPage = 10;
            return {
              total,
              items: toBeFlatten,
            };
          });

          // return result
        });
    },
    tableLoaded(result) {
      this.loaded = true;
      this.$emit("page", this.page);
    },
    flattenObj(ob) {
      // The object which contains the
      // final result
      const result = {};

      // loop through the object "ob"
      for (const i in ob) {
        // We check the type of the i using
        // typeof() function and recursively
        // call the function again
        if (typeof ob[i] === "object" && !Array.isArray(ob[i])) {
          const temp = this.flattenObj(ob[i]);
          for (const j in temp) {
            // Store temp in result
            result[i + "." + j] = temp[j];
          }

          continue;
        }

        // Else store ob[i] in result directly
        result[i] = ob[i];
      }
      return result;
    },

    flatten(obj, keys) {
      const clone = JSON.parse(JSON.stringify(obj));
      keys.forEach((key) => {
        if (key.startsWith("#")) {
          return;
        }
        key.split(".").reduce((acc, val) => {
          if (typeof acc !== "object") {
            return acc;
          }
          const v = acc[val];
          if (v instanceof Array) {
            acc[val] = v[0];
            return v[0];
          }
          return v;
        }, clone);
      });
      return clone;
    },
  },
};
</script>

<style lang="scss">
.chart--error {
  position: relative;
}

.visualization {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.break-text {
  overflow-wrap: break-word;
  word-wrap: break-word;
}

.visualization__content {
  flex: 1 1 auto;
  overflow-y: hidden;
}

.visualization__footer {
  flex-shrink: 0;
}

.popover__content {
  padding: 0;
}

.popover {
  max-width: unset;
}

.menu-item {
  border-top: 1px solid #888;
  background: #353945;
}

/* Responsive font scaling for sum widgets on desktop only */
@media screen and (min-width: 1024px) {
  .widget-sum-display {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 100px;
  }

  .widget-sum-number {
    font-size: clamp(1.5rem, 4vw, 3.5rem);
    line-height: 1.1;
    font-weight: bold;
    word-break: break-all;
    overflow-wrap: break-word;
    max-width: 100%;
    text-align: center;
  }

  .widget-sum-unit {
    font-size: clamp(1rem, 2.5vw, 2rem);
    line-height: 1.2;
    margin-top: 0.25rem;
    opacity: 0.8;
    word-break: break-all;
    overflow-wrap: break-word;
    max-width: 100%;
    text-align: center;
  }

  /* Ensure the widget container handles overflow properly */
  .visualization__content {
    overflow: hidden;
    padding: 0.5rem;
  }

  /* Additional responsive breakpoints for better scaling */
  @media screen and (min-width: 1216px) {
    .widget-sum-number {
      font-size: clamp(2rem, 3.5vw, 4rem);
    }

    .widget-sum-unit {
      font-size: clamp(1.25rem, 2vw, 2.5rem);
    }
  }

  @media screen and (min-width: 1408px) {
    .widget-sum-number {
      font-size: clamp(2.5rem, 3vw, 4.5rem);
    }

    .widget-sum-unit {
      font-size: clamp(1.5rem, 1.8vw, 3rem);
    }
  }
}
</style>
